import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
    theme: Theme;
    resolvedTheme: 'light' | 'dark';
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const { user, profile } = useAuth();
    const [theme, setThemeState] = useState<Theme>(() => {
        // Load from localStorage first for immediate apply
        const stored = localStorage.getItem('gofit-theme');
        return (stored as Theme) || 'system';
    });

    // Resolve system theme
    const getSystemTheme = (): 'light' | 'dark' => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return 'light';
    };

    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
        if (theme === 'system') return getSystemTheme();
        return theme;
    });

    // Load theme from profile when available
    useEffect(() => {
        if (profile) {
            const profileData = profile as any;
            if (profileData.theme_preference) {
                setThemeState(profileData.theme_preference);
                localStorage.setItem('gofit-theme', profileData.theme_preference);
            }
        }
    }, [profile]);

    // Update resolved theme when theme changes
    useEffect(() => {
        if (theme === 'system') {
            setResolvedTheme(getSystemTheme());
        } else {
            setResolvedTheme(theme);
        }
    }, [theme]);

    // Listen for system theme changes
    useEffect(() => {
        if (theme !== 'system') return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            setResolvedTheme(e.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    // Apply theme to document
    useEffect(() => {
        const root = document.documentElement;
        if (resolvedTheme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [resolvedTheme]);

    // Set theme with persistence
    const setTheme = useCallback(async (newTheme: Theme) => {
        setThemeState(newTheme);
        localStorage.setItem('gofit-theme', newTheme);

        // Persist to database if logged in
        if (user) {
            try {
                await supabase
                    .from('users')
                    .update({ theme_preference: newTheme })
                    .eq('id', user.id);
            } catch (error) {
                console.error('Error saving theme preference:', error);
            }
        }
    }, [user]);

    return (
        <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}
